import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import cn from 'classnames';
import { Tooltip } from '@material-ui/core';

import BrButton from '@components/BrButton';
import BrIcons from '@components/BrIcons';
import BrText from '@components/BrText';
import ColorStyles from '@components/ColorStyles';
import BrSearchBox from '@components/BrSearchBox';
import './brDropdownMenu.scss';

/* eslint-disable react/no-array-index-key */
export default class BrDropdownMenu extends PureComponent {
  static propTypes = {
    allowSelectNone: PropTypes.bool,
    btnColorStyle: PropTypes.oneOf(Object.values(ColorStyles)),
    className: PropTypes.string, // if not passed, selected items' title will be used
    defaultTitle: PropTypes.string,
    isDisabled: PropTypes.bool, // this will be used to highlight the selected item
    isReturnWholeItem: PropTypes.bool,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.element,
        isHeader: PropTypes.bool,
        title: PropTypes.string,
        value: PropTypes.any,
        dom: PropTypes.node,
        isDisabled: PropTypes.bool,
        toolTipText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      }),
    ),
    onSelect: PropTypes.func.isRequired, // the dropdown btn will be more btn like rather than dropdown like
    selected: PropTypes.any,
    title: PropTypes.any,
    tooltipText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    useBtnStyle: PropTypes.bool,
    useFirstAsDefault: PropTypes.bool, // (item.value || item) => {}
    useNewVariantDownCarrot: PropTypes.bool,
    useSearch: PropTypes.bool,
    placeholder: PropTypes.string,
  };

  static defaultProps = {
    allowSelectNone: false,
    items: [],
    btnColorStyle: ColorStyles.GRAY,
    useFirstAsDefault: false,
    isReturnWholeItem: false,
    isDisabled: false,
    defaultTitle: '',
    useNewVariantDownCarrot: false,
    useSearch: false,
    placeholder: 'Search',
  };

  constructor(props) {
    super(props);
    this.state = {
      isActive: false,
      selectedItem: this.getSelectedItem(props),
      allItems: this.props.items,
      query: '',
    }; // may use initial props
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { selected, items } = nextProps;
    if (selected !== this.props.selected || items !== this.props.items) {
      this.setState({ selectedItem: this.getSelectedItem(nextProps), allItems: items });
    }
  }

  getSelectedItem = (props) => {
    const { selected, items, useFirstAsDefault } = props;
    let selectedItem = selected || '';
    // allows passing item values for matching on selected item
    if (typeof selected === 'number' || typeof selected === 'string' || typeof selected === 'boolean') {
      selectedItem = items.find((item) => item.value === selected);
    }
    // if no match, set to first item from the list if so desired
    if (!selectedItem && useFirstAsDefault && items.length) {
      selectedItem = items[0];
    }
    return selectedItem;
  };

  handleSelectItem = (item) => {
    const { onSelect, isReturnWholeItem } = this.props;
    if (onSelect) {
      onSelect(item.value !== undefined && !isReturnWholeItem ? item.value : item);
    }
    this.setState({ isActive: false });
  };

  handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      this.setState({
        isActive: false,
        query: '',
        allItems: this.props.items,
      });
    }
  };

  handleClickBtn = () => {
    if (!this.props.isDisabled) {
      this.setState({ isActive: !this.state.isActive, query: '', allItems: this.props.items });
    }
  };

  renderDisplayTitle = (item) => {
    return (
      <div className={cn('item-title', { disabled: this.props.isDisabled, divider: item.divider })}>
        {item.icon}
        <div>
          <BrText>{item.title || item.value}</BrText>
        </div>
        {item.dom}
      </div>
    );
  };

  handleQueryChange = (queryString) => {
    this.setState({
      allItems: this.props.items.filter((item) => item.title
        .toString()
        .toLowerCase()
        .includes(queryString.toString().toLowerCase())),
      query: queryString,
    });
  };

  getItem = (item, key) => {
    const { selectedItem } = this.state;
    const itemWithoutHover = (
      <div
        key={key}
        className={cn('dropdown-item', {
          'is-selected': item === selectedItem,
          disabled: item.isDisabled,
        })}
        onClick={() => this.handleSelectItem(item)}
        title={item.title || item.value}
      >
        {this.renderDisplayTitle(item)}
      </div>
    );

    return item.toolTipText ? (
      <Tooltip arrow title={item.toolTipText}>
        <span>{itemWithoutHover}</span>
      </Tooltip>
    ) : (
      itemWithoutHover
    );
  }

  render() {
    const {
      allowSelectNone,
      title,
      defaultTitle,
      className,
      useBtnStyle,
      btnColorStyle,
      isDisabled,
      tooltipText,
      useNewVariantDownCarrot,
      useSearch,
      placeholder,
    } = this.props;
    const { isActive, selectedItem, allItems, query } = this.state;
    const classname = cn('br-dropdown-menu', className, {
      active: isActive,
      disabled: isDisabled,
    });

    // set dropdown btn title
    let btnTitle = title || (selectedItem && this.renderDisplayTitle(selectedItem)) || defaultTitle;
    let btnTitleText = '';
    if (typeof title === 'string') {
      btnTitleText = title;
    } else if (selectedItem) {
      btnTitleText = selectedItem.title || selectedItem.value;
    } else {
      btnTitleText = defaultTitle;
    }
    if (!useBtnStyle) {
      if (typeof btnTitle === 'string') {
        btnTitle = <div className={cn('item-title', isDisabled && 'disabled')}>{btnTitle}</div>;
      }
      btnTitle = (
        <div className="dropdown-title" title={btnTitleText}>
          {btnTitle}
          {useNewVariantDownCarrot ? <BrIcons.DownCarrotV2 /> : <BrIcons.DownCaret isSubComp />}
        </div>
      );
    }

    if (allowSelectNone && allItems.length > 0 && allItems[0].id !== 0) {
      allItems.unshift({
        id: 0,
        title: 'None',
        value: null,
        icon: null,
      });
    }

    return (
      <div className={classname} tabIndex="0" onBlur={this.handleBlur} data-id="br-dropdown-menu">
        <BrButton
          title={btnTitle}
          onClick={this.handleClickBtn}
          isSubComp
          colorStyle={btnColorStyle}
          tooltipText={tooltipText}
        />

        <div className={cn('dropdown-item-list', { 'search-list': useSearch })}>

          {useSearch && (
          <BrSearchBox
            className={cn('item-search-box', { 'search-box-item': useSearch })}
            placeholder={placeholder}
            onQueryChange={this.handleQueryChange}
            onQuerySubmit={this.handleQueryChange}
            query={query}
          />
          )}
          <div className={cn({ 'dropdown-container': useSearch })}>
            {allItems.map((item, idx) => (
              this.getItem(item, idx)
            ))}
          </div>
        </div>
      </div>
    );
  }
}
